import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  Clock,
  ShieldCheck,
  HelpCircle,
  ArrowRight,
  Send,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: `Centro de ayuda — ${SITE.brand.name}` },
      {
        name: "description",
        content:
          "Habla con un asesor real por WhatsApp, teléfono o email. Atención 24/7 antes, durante y después de tu reserva.",
      },
      { property: "og:title", content: `Centro de ayuda — ${SITE.brand.name}` },
      { property: "og:url", content: "/help" },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/help" }],
  }),
  component: HelpPage,
});

const TOPICS = [
  "Reservar un tour",
  "Cambios o cancelación",
  "Pagos y facturación",
  "Información de un destino",
  "Recoger voucher / punto de encuentro",
  "Otro",
];

function HelpPage() {
  const [sending, setSending] = useState(false);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSending(true);
    const form = new FormData(e.currentTarget);
    const name = String(form.get("name") ?? "").trim();
    const email = String(form.get("email") ?? "").trim();
    const phone = String(form.get("phone") ?? "").trim();
    const topic = String(form.get("topic") ?? "").trim();
    const message = String(form.get("message") ?? "").trim();

    if (!name || !email || !message) {
      toast.error("Completa nombre, email y mensaje.");
      setSending(false);
      return;
    }

    const subject = `[Ayuda] ${topic || "Consulta"} — ${name}`;
    const body =
      `Nombre: ${name}\n` +
      `Email: ${email}\n` +
      `Teléfono: ${phone || "—"}\n` +
      `Tema: ${topic || "—"}\n\n` +
      `Mensaje:\n${message}\n\n— Enviado desde mostrandomexico.com/help`;

    const recipients = SITE.contact.emails.map((e) => e.address).join(",");
    const mailto = `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Also offer WhatsApp as immediate fallback
    const waText = encodeURIComponent(
      `Hola, soy ${name}.\nMi correo: ${email}\nTema: ${topic || "—"}\n\n${message}`,
    );

    window.location.href = mailto;
    toast.success("Abrimos tu correo. Si prefieres respuesta inmediata, escríbenos por WhatsApp.");
    // Stash WA link so users can copy it
    setTimeout(() => {
      window.open(`${SITE.contact.whatsappUrl}?text=${waText}`, "_blank", "noopener,noreferrer");
      setSending(false);
    }, 600);
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Hero */}
        <header className="text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            Centro de ayuda · {SITE.contact.hours}
          </p>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            ¿En qué te ayudamos?
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            Tienes a un asesor humano a un mensaje de distancia. Elige el canal que prefieras —respondemos
            en minutos vía WhatsApp.
          </p>
        </header>

        {/* Primary contact rail */}
        <section className="mt-10 grid gap-4 sm:grid-cols-3" aria-label="Canales de contacto">
          <a
            href={SITE.contact.whatsappPrettyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative overflow-hidden rounded-2xl border border-border bg-[oklch(0.97_0.05_150)] p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#25D366] text-white shadow-md">
                <MessageCircle className="h-6 w-6" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[oklch(0.35_0.12_150)]">
                  WhatsApp · más rápido
                </p>
                <p className="font-display text-xl font-semibold text-foreground">
                  {SITE.contact.phone}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-foreground/75">
              Chatea con un asesor real. Disponible 24/7, también en inglés.
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[oklch(0.35_0.12_150)]">
              Abrir WhatsApp{" "}
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
            </span>
          </a>

          <a
            href={`tel:${SITE.contact.phoneTel}`}
            className="group rounded-2xl border border-border bg-card p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                <Phone className="h-6 w-6" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Llamada directa
                </p>
                <p className="font-display text-xl font-semibold text-foreground">
                  {SITE.contact.phone}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Para urgencias durante el viaje o ajustes de último minuto.
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
              Llamar ahora{" "}
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
            </span>
          </a>

          <a
            href={`mailto:${SITE.contact.assistantEmail}`}
            className="group rounded-2xl border border-border bg-card p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-foreground text-background shadow-md">
                <Mail className="h-6 w-6" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Correo electrónico
                </p>
                <p className="font-display text-lg font-semibold text-foreground">
                  Te respondemos en horas
                </p>
              </div>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {SITE.contact.emails.map((e) => (
                <li key={e.address}>
                  <span className="font-medium text-foreground/80">{e.label}:</span>{" "}
                  <a className="text-primary hover:underline" href={`mailto:${e.address}`}>
                    {e.address}
                  </a>
                </li>
              ))}
            </ul>
          </a>
        </section>

        {/* Form + sidebar */}
        <section className="mt-12 grid gap-8 lg:grid-cols-[1.2fr_1fr]">
          <form
            onSubmit={onSubmit}
            aria-labelledby="help-form-title"
            className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-8"
          >
            <h2 id="help-form-title" className="font-display text-2xl font-semibold">
              Cuéntanos qué necesitas
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Tu mensaje llega a nuestro equipo de asesores y al concierge Maya. Te respondemos por
              email y WhatsApp.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field id="name" name="name" label="Nombre completo" placeholder="Tu nombre" required />
              <Field
                id="email"
                name="email"
                type="email"
                label="Correo electrónico"
                placeholder="tu@email.com"
                required
              />
              <Field
                id="phone"
                name="phone"
                type="tel"
                label="WhatsApp / Teléfono (opcional)"
                placeholder="+52 998 …"
              />
              <div>
                <label
                  htmlFor="topic"
                  className="block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Tema
                </label>
                <select
                  id="topic"
                  name="topic"
                  defaultValue=""
                  className="mt-1.5 h-11 w-full rounded-xl border border-input bg-background px-3 text-sm text-foreground outline-none transition focus:border-primary focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="" disabled>
                    Elige un tema
                  </option>
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label
                htmlFor="message"
                className="block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
              >
                Mensaje
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                placeholder="Cuéntanos fechas tentativas, destino, número de personas o tu duda…"
                className="mt-1.5 w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none transition focus:border-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-muted-foreground">
                Al enviar aceptas nuestra{" "}
                <Link to="/faq" className="underline hover:text-primary">
                  política de privacidad
                </Link>
                .
              </p>
              <button
                type="submit"
                disabled={sending}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-glow transition hover:bg-primary-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              >
                <Send className="h-4 w-4" aria-hidden="true" /> Enviar mensaje
              </button>
            </div>
          </form>

          <aside className="space-y-4">
            <InfoCard icon={Clock} title="Horario">
              {SITE.contact.hours} — incluyendo fines de semana y festivos.
            </InfoCard>
            <InfoCard icon={MapPin} title="Oficina en Cancún">
              {SITE.contact.address}
            </InfoCard>
            <InfoCard icon={HelpCircle} title="¿Buscas respuestas rápidas?">
              Revisa nuestras{" "}
              <Link to="/faq" className="font-semibold text-primary hover:underline">
                preguntas frecuentes
              </Link>{" "}
              sobre cancelación, pagos y descuentos vigentes.
            </InfoCard>
            <div className="rounded-2xl border border-primary/30 bg-accent/40 p-5 text-sm">
              <p className="font-display text-lg font-semibold text-accent-foreground">
                ¿Ya tienes reserva?
              </p>
              <p className="mt-1 text-accent-foreground/80">
                Localiza tu voucher con el código de confirmación. Disponible 24/7 en tu correo.
              </p>
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary"
              >
                Ir a postventa <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  id,
  name,
  label,
  type = "text",
  placeholder,
  required,
}: {
  id: string;
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
      >
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="mt-1.5 h-11 w-full rounded-xl border border-input bg-background px-4 text-sm outline-none transition focus:border-primary focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}

function InfoCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Mail;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{children}</p>
        </div>
      </div>
    </div>
  );
}
